import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { ClockIcon, DeleteIcon, PhoneIcon, VoicemailIcon } from '@sharedCR/coreDesignSystem/icons';
import { telephonyApi } from 'app/services/api';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import { TELNYX_SIP_DOMAIN } from 'constants/telephony';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';

import {
  deleteVoicemailAction,
  fetchVoicemailsAction,
  makeOutgoingCallAction,
  markAllVoicemailsReadAction,
  markVoicemailReadAction,
} from 'store/system/actions';
import {
  selectVoicemails,
  selectVoicemailsLoading,
  selectVoicemailUnreadCount,
} from 'store/system/selectors';

import {
  actionButtonStyles,
  actionsRowStyles,
  callerNameReadStyles,
  callerNameStyles,
  deleteButtonStyles,
  durationIconStyles,
  emptyStateIconStyles,
  emptyStateStyles,
  emptyStateTextStyles,
  headerRowStyles,
  headerTitleStyles,
  listContainerStyles,
  loadingContainerStyles,
  markAllReadButtonStyles,
  playerRowStyles,
  readDotPlaceholderStyles,
  unreadDotStyles,
  voicemailContainerStyles,
  voicemailDetailStyles,
  voicemailInfoStyles,
  voicemailRowStyles,
  voicemailRowUnreadStyles,
  voicemailTimeStyles,
} from './Voicemails.styles';
import { VoicemailsTestIds } from './Voicemails.testIds';

const audioPlayerInlineStyles = { width: '100%', height: 32 };
const MS_PER_MINUTE = 60000;
const MS_PER_HOUR = 3600000;
const MS_PER_DAY = 86400000;
const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;
const DAYS_PER_WEEK = 7;
const SECONDS_PER_MINUTE = 60;

const formatVoicemailTime = isoString => {
  if (!isoString) {
    return '';
  }

  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / MS_PER_MINUTE);
  const diffHours = Math.floor(diffMs / MS_PER_HOUR);
  const diffDays = Math.floor(diffMs / MS_PER_DAY);

  if (diffMins < 1) {
    return 'Just now';
  }

  if (diffMins < MINUTES_PER_HOUR) {
    return `${diffMins}m ago`;
  }

  if (diffHours < HOURS_PER_DAY) {
    return `${diffHours}h ago`;
  }

  if (diffDays === 1) {
    return 'Yesterday';
  }

  if (diffDays < DAYS_PER_WEEK) {
    return `${diffDays}d ago`;
  }

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatDuration = seconds => {
  if (!seconds || seconds <= 0) {
    return '0s';
  }

  const mins = Math.floor(seconds / SECONDS_PER_MINUTE);
  const secs = Math.round(seconds % SECONDS_PER_MINUTE);

  if (mins === 0) {
    return `${secs}s`;
  }

  return `${mins}m ${secs}s`;
};

const VoicemailRow = memo(({ voicemail, isExpanded, onToggle, onCallback, onDelete }) => {
  const dispatch = useDispatch();
  const { voicemailId, callerName, callerNumber, createdAt, duration, isRead } = voicemail;

  const displayName = callerName || callerNumber || 'Unknown';
  const time = formatVoicemailTime(createdAt);
  const durationText = formatDuration(duration);

  const [audioUrl, setAudioUrl] = useState(null);
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  const isFetchingRef = useRef(false);

  const handleToggle = useCallback(() => {
    onToggle(voicemailId);

    if (!isRead) {
      dispatch(markVoicemailReadAction({ voicemailId }));
    }
  }, [voicemailId, isRead, onToggle, dispatch]);

  const handleCallback = useCallback(
    e => {
      e.stopPropagation();
      onCallback(voicemail);
    },
    [voicemail, onCallback],
  );

  const handleDelete = useCallback(
    e => {
      e.stopPropagation();
      onDelete(voicemailId);
    },
    [voicemailId, onDelete],
  );

  useEffectSafeStrictMode(() => {
    if (!isExpanded || audioUrl || isFetchingRef.current) {
      return;
    }

    const ctrl = { active: true };

    isFetchingRef.current = true;
    setIsLoadingAudio(true);
    telephonyApi
      .getVoicemailAudioUrl({ voicemailId })
      .then(({ data }) => {
        if (ctrl.active) {
          setAudioUrl(data?.url ?? data?.value ?? data);
        }
      })
      .catch(() => {
        if (ctrl.active) {
          setAudioUrl(null);
        }
      })
      .finally(() => {
        isFetchingRef.current = false;

        if (ctrl.active) {
          setIsLoadingAudio(false);
        }
      });

    return () => {
      ctrl.active = false;
    };
  }, [isExpanded, voicemailId, audioUrl]);

  return (
    <>
      <Box sx={isRead ? voicemailRowStyles : voicemailRowUnreadStyles} onClick={handleToggle}>
        <Box sx={isRead ? readDotPlaceholderStyles : unreadDotStyles} />
        <Box sx={voicemailInfoStyles}>
          <Typography sx={isRead ? callerNameReadStyles : callerNameStyles} variant="regular14">
            {displayName}
          </Typography>
          <Box sx={voicemailDetailStyles}>
            <Box sx={durationIconStyles}>
              <ClockIcon size={IconSize[14]} />
            </Box>
            <Typography variant="regular12">{durationText}</Typography>
          </Box>
        </Box>
        <Typography sx={voicemailTimeStyles} variant="regular12">
          {time}
        </Typography>
      </Box>

      {isExpanded && (
        <Box sx={playerRowStyles}>
          {isLoadingAudio && <CircularProgress size={20} />}
          {audioUrl && <audio controls src={audioUrl} style={audioPlayerInlineStyles} />}
          <Box sx={actionsRowStyles}>
            {callerNumber && (
              <Button
                data-testid={VoicemailsTestIds.CALLBACK}
                size="small"
                sx={actionButtonStyles}
                variant="text"
                onClick={handleCallback}
              >
                <PhoneIcon className="mr-4" size={IconSize[14]} />
                Call back
              </Button>
            )}
            <Button
              data-testid={VoicemailsTestIds.DELETE}
              size="small"
              sx={deleteButtonStyles}
              variant="text"
              onClick={handleDelete}
            >
              <DeleteIcon className="mr-4" size={IconSize[14]} />
              Delete
            </Button>
          </Box>
        </Box>
      )}
    </>
  );
});

VoicemailRow.displayName = 'VoicemailRow';

VoicemailRow.propTypes = {
  voicemail: PropTypes.shape({
    voicemailId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    callerName: PropTypes.string,
    callerNumber: PropTypes.string,
    createdAt: PropTypes.string,
    duration: PropTypes.number,
    isRead: PropTypes.bool,
  }).isRequired,
  isExpanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onCallback: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

const Voicemails = () => {
  const dispatch = useDispatch();
  const voicemails = useSelector(selectVoicemails);
  const isLoading = useSelector(selectVoicemailsLoading);
  const unreadCount = useSelector(selectVoicemailUnreadCount);

  const [expandedId, setExpandedId] = useState(null);

  useEffectSafeStrictMode(() => {
    dispatch(fetchVoicemailsAction());
  }, [dispatch]);

  const onToggle = useCallback(voicemailId => {
    setExpandedId(prev => (prev === voicemailId ? null : voicemailId));
  }, []);

  const onCallback = useCallback(
    voicemail => {
      const destinationNumber = voicemail.userId
        ? `${voicemail.callerNumber}@${TELNYX_SIP_DOMAIN}`
        : voicemail.callerNumber;

      dispatch(makeOutgoingCallAction({ destinationNumber }));
    },
    [dispatch],
  );

  const onDelete = useCallback(
    voicemailId => {
      dispatch(deleteVoicemailAction({ voicemailId }));
      setExpandedId(prev => (prev === voicemailId ? null : prev));
    },
    [dispatch],
  );

  const onMarkAllRead = useCallback(() => {
    dispatch(markAllVoicemailsReadAction());
  }, [dispatch]);

  const voicemailList = useMemo(
    () =>
      voicemails.map(voicemail => (
        <VoicemailRow
          isExpanded={expandedId === voicemail.voicemailId}
          key={voicemail.voicemailId}
          voicemail={voicemail}
          onCallback={onCallback}
          onDelete={onDelete}
          onToggle={onToggle}
        />
      )),
    [voicemails, expandedId, onToggle, onCallback, onDelete],
  );

  if (isLoading) {
    return (
      <Box sx={voicemailContainerStyles}>
        <Box sx={loadingContainerStyles}>
          <CircularProgress size={32} />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={voicemailContainerStyles}>
      <Box sx={headerRowStyles}>
        <Typography sx={headerTitleStyles} variant="regular14">
          Voicemail {voicemails.length > 0 && `(${voicemails.length})`}
        </Typography>
        {unreadCount > 0 && (
          <Button
            data-testid={VoicemailsTestIds.MARK_ALL_READ}
            size="small"
            sx={markAllReadButtonStyles}
            variant="text"
            onClick={onMarkAllRead}
          >
            Mark all read
          </Button>
        )}
      </Box>

      {voicemails.length === 0 ? (
        <Box sx={emptyStateStyles}>
          <Box sx={emptyStateIconStyles}>
            <VoicemailIcon size={IconSize[48]} />
          </Box>
          <Typography sx={emptyStateTextStyles} variant="regular14">
            No voicemails yet
          </Typography>
        </Box>
      ) : (
        <Box sx={listContainerStyles}>{voicemailList}</Box>
      )}
    </Box>
  );
};

export default memo(Voicemails);
